import { array } from 'fp-ts';
import { flow } from 'fp-ts/function';
import { __, every, filter, get, groupBy, map, mapValues, mergeWith, reduce, rest, set, values } from 'lodash/fp';
import { createSelector } from 'reselect';
import { AmountTaxValue, OrderItemType, PaymentProviderUsageMethod, PaymentState } from '@mews-commander/data-access';
import {
    Amount,
    ApplicationState,
    OrderItem,
    PreauthorizationState,
    TaxRate,
    TaxRateStrategyDiscriminator,
    WithAccountId,
} from '@mews-commander/types';
import { getProp } from '@mews/fp';
import { AppClockServiceProps, composeProps, createPropsExtender, DecimalService } from '@mews/framework';
import { FeatureFlag } from '@mews-commander/enums';
import { getFeatureFlagService } from '@mews-commander/legacy-app-instance';
import { dummyAmount, getNowUtc, isAmountPositive, isZeroAmount, notEmpty, sumAmounts } from '@mews-commander/utils';
import { getCurrentEnterpriseId } from '@mews-commander/core';
import {
    getPaymentsByBillId,
    getPaymentsByBillIds,
    getPaymentsByIds,
} from '@mews-commander/legacy-entities/src/ducks/payments';
import {
    getCurrentEnterpriseDefaultCurrencyCode,
    isCurrentEnterpriseInMultiTaxEnvironment,
} from '@mews-commander/legacy-entities/src/selectors/session/enterprise';
import {
    getCustomerGatewayPaymentCards,
    getOrderItemsByBillId,
    getOrderItemsByIds,
    getPreauthorization,
    getPreauthorizations,
    isSurchargingEnabled,
} from '@mews-commander/legacy-entities/src/ducks';
import { hasSurchargeFee } from '@mews-commander/legacy-entities/src/ducks/accountingConfigurations';
import {
    getBalancedAmountFromCurrencyMap,
    getTotalAmountAndCount,
    mergeCurrencyMaps,
    sumAmountsIntoCurrencyMap,
} from '../utils';
import { AccountBillAmounts } from '../types';
import { getSelectedOrderItemIds, getSelectedPaymentIds } from '../ducks/billItems';
import { isWithBankSepaPaymentMethod } from '../BillCardList/BillCard/PaymentList/PaymentItem/utils';
import { getPaymentCard, getPaymentCardType, isPaymentCardPhysical } from './paymentCards';
import { getMonthOrdersServiceOrders, getOpenOrClosedOrderItemsByBillIds } from './orderItems';
import { getBillOrderItemsByServiceOrderIds, getOrderItemsByGroupId } from './grouping';

export const getZeroAmount = createSelector(
    getCurrentEnterpriseDefaultCurrencyCode as (state: ApplicationState) => string,
    set('currency', __, dummyAmount)
);

const getNotCancelledOrderItemAmounts = createSelector(getOpenOrClosedOrderItemsByBillIds, map('totalAmount'));

const getChargedPaymentsByBillIds = createSelector(getPaymentsByBillIds, filter({ state: PaymentState.Charged }));

const getWithBankSepaPaymentsByBillIds = createSelector(getPaymentsByBillIds, (payments) =>
    payments.filter(isWithBankSepaPaymentMethod)
);

const getPaymentAmounts = createSelector(getChargedPaymentsByBillIds, map('value'));

const reduceSumAmounts = (acc: Amount, amounts: Amount[]) => reduce(sumAmounts, acc, amounts);

const getPaidAmount = createSelector(getZeroAmount, getPaymentAmounts, reduceSumAmounts);

export const getAmountDue = createSelector(getPaidAmount, getNotCancelledOrderItemAmounts, reduceSumAmounts);

const getChargedPaymentAmount = (payment: unknown) => {
    const isCheckForMultiCurrencyBillAmountEnabled = getFeatureFlagService().hasFlag(
        FeatureFlag.withCheckForMultiCurrencyBillAmountEnabled
    );

    if (isCheckForMultiCurrencyBillAmountEnabled) {
        return get('data.value.multiCurrencyBillAmount', payment) ?? get('value', payment);
    }

    return get('value', payment);
};

const getChargedPaymentAmountsPerBillId = createSelector(
    getChargedPaymentsByBillIds,
    flow(groupBy('billId'), mapValues(map(getChargedPaymentAmount)))
);

const getWithBankSepaPaymentAmountsPerBillId = createSelector(
    getWithBankSepaPaymentsByBillIds,
    flow(groupBy('billId'), mapValues(map('value')))
);

const getOpenOrderTotalAmountsPerBillId = createSelector(
    getOpenOrClosedOrderItemsByBillIds,
    flow(groupBy('billId'), mapValues(map('totalAmount')))
);

export const calculateTotalAmount = (
    amounts: Amount[],
    zeroAmount: Amount,
    defaultCurrencyCode: string,
    isMultiTaxEnvironment: boolean
): Amount => {
    const summedOrderAmounts = sumAmountsIntoCurrencyMap(amounts);
    const totalAmount = getBalancedAmountFromCurrencyMap(
        summedOrderAmounts,
        defaultCurrencyCode,
        isMultiTaxEnvironment
    );

    return totalAmount || zeroAmount;
};

export const calculateBillAmounts = (
    billIds: string[],
    billChargedPaymentAmounts: Record<string, Array<Amount>>,
    billWithBankSepaPaymentAmounts: Record<string, Array<Amount>>,
    billOpenOrderTotalAmounts: Record<string, Array<Amount>>,
    zeroAmount: Amount,
    defaultCurrencyCode: string,
    isMultiTaxEnvironment: boolean
): AccountBillAmounts => {
    if (billIds.length === 0) {
        return { billAmounts: {}, totalAmount: zeroAmount };
    }

    const isWithBankCloseBillEnabled = getFeatureFlagService().hasFlag(FeatureFlag.withBankCloseBillEnabled);

    const summedChargedPaymentAmounts = mapValues(sumAmountsIntoCurrencyMap, billChargedPaymentAmounts);
    const summedWithBankAmounts = mapValues(
        sumAmountsIntoCurrencyMap,
        isWithBankCloseBillEnabled ? billWithBankSepaPaymentAmounts : {}
    );
    const summedPaymentAmounts = mergeWith(mergeCurrencyMaps, summedChargedPaymentAmounts, summedWithBankAmounts);

    const summedOrderAmounts = mapValues(sumAmountsIntoCurrencyMap, billOpenOrderTotalAmounts);

    const mergedAmounts = mergeWith(mergeCurrencyMaps, summedPaymentAmounts, summedOrderAmounts);

    const billAmounts = reduce<string, Record<string, Amount>>(
        (acc, billId) => {
            acc[billId] =
                getBalancedAmountFromCurrencyMap(mergedAmounts[billId], defaultCurrencyCode, isMultiTaxEnvironment) ||
                zeroAmount;

            return acc;
        },
        {},
        billIds
    );

    const totalAmount = reduceSumAmounts(zeroAmount, values(billAmounts));

    return { billAmounts, totalAmount };
};

export const getAmountDueForBillIds = createSelector(
    getProp<string[]>()('billIds'),
    getChargedPaymentAmountsPerBillId,
    getWithBankSepaPaymentAmountsPerBillId,
    getOpenOrderTotalAmountsPerBillId,
    getZeroAmount,
    getCurrentEnterpriseDefaultCurrencyCode,
    isCurrentEnterpriseInMultiTaxEnvironment,
    calculateBillAmounts
);

const hasBillPayments = flow(getPaymentsByBillId, notEmpty);

const hasBillOrderItems = flow(getOrderItemsByBillId, notEmpty);

const hasPaymentCard = flow(getCustomerGatewayPaymentCards, notEmpty);

interface IsBillChargableProps extends WithAccountId, AppClockServiceProps {
    amount?: Amount;
}

export const isBillChargeable = (
    state: ApplicationState,
    { accountId, appClockService, amount }: IsBillChargableProps
) => (amount ? isAmountPositive(amount) && hasPaymentCard(state, { accountId, appClockService }) : false);

interface IsBillClosableProps {
    billId: string;
    amount?: Amount;
}

export const isBillClosable = (state: ApplicationState, { amount, billId }: IsBillClosableProps) => {
    if (!amount || !isZeroAmount(amount)) {
        return false;
    }

    const enterpriseId = getCurrentEnterpriseId();

    return hasBillPayments(state, { billId, enterpriseId }) || hasBillOrderItems(state, { billId });
};

export const getChargeablePreauthorizations = createSelector(
    getPreauthorizations,
    getNowUtc,
    (preauthorizations, nowUtc) =>
        filter(
            ({ state, expirationUtc, usageMethod }) =>
                state === PreauthorizationState.Preauthorized &&
                expirationUtc >= nowUtc &&
                usageMethod === PaymentProviderUsageMethod.Gateway,
            preauthorizations
        )
);

export const getChargeablePreauthorizationIds = flow(getChargeablePreauthorizations, map('id'));

export const shouldShowSurcharge = createSelector(
    isPaymentCardPhysical,
    isSurchargingEnabled,
    composeProps(hasSurchargeFee, createPropsExtender('paymentCardType', getPaymentCardType)),
    rest(every(Boolean))
);

const getPreauthorizationPaymentCardId = createSelector(getPreauthorization, get('paymentCardId'));

export const getPaymentCardByPreauthorization = composeProps(
    getPaymentCard,
    createPropsExtender('id', getPreauthorizationPaymentCardId)
);

const getTotalAmountAndCountForOrderItemsAndPayments = (orderItems: OrderItem[] = []) =>
    getTotalAmountAndCount([
        ...orderItems.map((order) => ({
            amount: order.totalAmount,
            unitCount: order.data.discriminator === OrderItemType.AllowanceDiscount ? 0 : order.unitMultiplier,
        })),
    ]);

export const getGroupedOrderItemsTotal = createSelector(
    getOrderItemsByGroupId,
    getTotalAmountAndCountForOrderItemsAndPayments
);

export const getGroupedByServiceOrderTotal = createSelector(
    getBillOrderItemsByServiceOrderIds,
    getTotalAmountAndCountForOrderItemsAndPayments
);

export const getGroupedByServiceOrderTotalWithoutPayments = createSelector(
    getBillOrderItemsByServiceOrderIds,
    getTotalAmountAndCountForOrderItemsAndPayments
);

export const getMonthGroupedOrderItemsTotal = createSelector(
    getMonthOrdersServiceOrders,
    getTotalAmountAndCountForOrderItemsAndPayments
);

const getSelectedOrderItems = composeProps<ApplicationState, {}, { ids: string[] }, OrderItem[]>(
    getOrderItemsByIds,
    createPropsExtender('ids', getSelectedOrderItemIds)
);

const getSelectedOrderItemsTotalAmounts = createSelector(
    getSelectedOrderItems,
    array.map(({ totalAmount }) => totalAmount)
);

export const getSelectedOrderItemsTotalAmount = createSelector(
    getSelectedOrderItemsTotalAmounts,
    getZeroAmount,
    getCurrentEnterpriseDefaultCurrencyCode,
    isCurrentEnterpriseInMultiTaxEnvironment,
    calculateTotalAmount
);

const getSelectedPayments = composeProps(getPaymentsByIds, createPropsExtender('ids', getSelectedPaymentIds));

const getSelectedPaymentsTotalAmounts = createSelector(
    getSelectedPayments,
    array.map(({ value }) => value)
);

export const getSelectedTotalAmount = createSelector(
    getSelectedOrderItemsTotalAmounts,
    getSelectedPaymentsTotalAmounts,
    getZeroAmount,
    getCurrentEnterpriseDefaultCurrencyCode,
    isCurrentEnterpriseInMultiTaxEnvironment,
    (orderItemsTotalAmounts, paymentsTotalAmounts, zeroAmount, currencyCode: string, isMultiTaxEnvironment: boolean) =>
        calculateTotalAmount(
            [...orderItemsTotalAmounts, ...paymentsTotalAmounts],
            zeroAmount,
            currencyCode,
            isMultiTaxEnvironment
        )
);

export const calculateTaxValue = (taxRateCode: string, taxRates: TaxRate[], unitCost: number): AmountTaxValue => {
    const selectedTaxRate = taxRates.find((taxRate) => taxRate.code === taxRateCode);

    if (!selectedTaxRate) return { taxRateCode, value: 0 };

    const taxRateValue = selectedTaxRate?.strategy.value.value;

    return selectedTaxRate.strategy.discriminator === TaxRateStrategyDiscriminator.Flat
        ? { taxRateCode, value: taxRateValue }
        : { taxRateCode, value: calculateTaxRateValueFromPercentage(unitCost, taxRateValue) };
};

const calculateTaxRateValueFromPercentage = (unitCost: number, taxRateValue: number) => {
    const percentageAmount = DecimalService.multiply(
        DecimalService.parse(unitCost),
        DecimalService.parse(taxRateValue)
    );
    const rounded = DecimalService.round(percentageAmount, 2);
    return DecimalService.toNumber(rounded);
};
